import { IGame } from "../types/game";
import GetNextState from "./get-next-state";
import ProcessKeyState from "./process-key-state";
import DeadState from "./dead-state";
import GameState from "./game-state";
import { CanvasStatus } from "../types";
import WaitBlockState from "./wait-block-state";

/**
 * NormalState is the persistent base state that stays on the stack.
 * It continuously manages the game flow by calling GetNextState or ProcessKeyState.
 * This matches the original C++ Player_normal behavior.
 */
export default class NormalState extends GameState {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(game: IGame) {
    super(game);
    // Match original Player_normal constructor: set idle and state
    // Original: canvas->idle = 1; canvas->state = Canvas::PLAYING; (lines 221-222)
    this.canvas.idle = 1;
    this.canvas.status = CanvasStatus.PLAYING;
  }

  enter(): void {
    // Match original Player_normal::init(): empty (no override in original)
    // All initialization is done in constructor
  }

  update(frameTime: number): void {
    super.update(frameTime);

    // NormalState should push a state every frame it runs
    // (matches original Player_normal::step() behavior)
    // Note: Game.loop() only calls update() on the top state, so this will only run when NormalState is top
    // Note: Original does NOT check for bonus here - bonus is only checked in Player_stamp, Player_dead_wait, and Player_wait_respawn

    // If no block, get next block (matching original Player_normal::step())
    if (!this.canvas.bloc) {
      // Log occasionally to debug piece spawning
      if (Math.random() < 0.1) { // 10% of the time
        console.log(`[NormalState] No bloc, calling GetNextState (isLocal=${this.game.player.isLocal()}, numPlayer=${this.canvas.numPlayer})`);
      }
      this.call(new GetNextState(this.game));
      return; // Early return to prevent further processing
    }
    
    // Block exists, proceed with normal gameplay
    // If this is a local player, check death condition
    if (this.game.player.isLocal()) {
      // Only check collision if block exists and player is actually playing (idle=1)
      // In survivor mode, if idle=2 (dead/waiting), we shouldn't be in NormalState
      // but add guard just in case
      if (this.canvas.idle === 1 && this.canvas.bloc) {
        if (
          this.canvas.checkCollide(
            this.canvas.bloc,
            this.canvas.bloc.x,
            this.canvas.bloc.y,
            this.canvas.bloc.rotation
          )
        ) {
          this.call(new DeadState(this.game));
        } else {
          this.call(new ProcessKeyState(this.game));
        }
      }
    } else {
      // Not local, use WaitBlockState (matches original Player_wait_block)
      // CRITICAL: In the original, Player_normal calls call(new Player_wait_block()) every frame
      // But Player_wait_block doesn't call ret(), so it stays on the stack
      // When a state is added via call(), it becomes the top state
      // So Player_wait_block becomes the top state and is called every frame
      // Player_normal is below it and is NOT called again (until Player_wait_block is removed)
      // 
      // IMPORTANT: If WaitBlockState already exists on the stack, don't create a new one
      // Creating a new one every frame would reset moveIndex, preventing move processing
      // Check if WaitBlockState is already on the stack
      const executor = this.getParentExecutor();
      if (executor) {
        const topState = executor.getTopState();
        // If top state is already WaitBlockState, don't create a new one
        // This preserves moveIndex and allows moves to be processed correctly
        if (topState && topState.toString() === "Wait Block") {
          // WaitBlockState is already on top, it will be called by the executor
          return;
        }
      }
      // WaitBlockState not on stack, create it (first time or after it was removed)
      this.call(new WaitBlockState(this.game));
    }
  }

  toString(): string {
    return "Normal";
  }
}
